// RiderDashboard.tsx - Fixed similarly
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Leaf, LogOut, Bell } from 'lucide-react';
import { CreateRiderRideRequestPayload, DestinationType, Ride, UserProfile, RiderTabType, Conversation } from '@/types';
import { ridesApi, riderRideRequestsApi, messagesApi, feedApi, notificationsApi, profileApi, getApiErrorMessage } from '@/lib/api';
import { useRealtime } from '@/hooks/useRealtime';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import BottomNav from '@/components/BottomNav';
import RideCard from '@/components/RideCard';
import FilterChips from '@/components/FilterChips';
import ChatPanel from '@/components/ChatPanel';
import ProfileCard from '@/components/ProfileCard';
import SafetyFooter from '@/components/SafetyFooter';
import ConversationList from '@/components/ConversationList';
import FeedsTab from '@/components/FeedsTab';
import PostModal from '@/components/PostModal';
import NotificationsPanel from '@/components/NotificationsPanel';
import ProfileEditModal from '@/components/ProfileEditModal';
import { RiderRideRequestForm } from '@/components/RiderRideRequestForm';
import { RiderRideRequestCard } from '@/components/RiderRideRequestCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface RiderDashboardProps {
  currentUser: UserProfile;
  onLogout: () => void;
  onUserUpdate?: (updatedUser: UserProfile) => void;
}

const RIDER_TABS: readonly RiderTabType[] = ['home', 'feeds', 'messages', 'profile'];

const RiderDashboard = ({ currentUser, onLogout, onUserUpdate }: RiderDashboardProps) => {
  // DEBUG: Log when RiderDashboard renders
  console.log('[DEBUG] RiderDashboard rendering, currentUser:', currentUser);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check for openChat query param to open chat directly
  const searchParams = new URLSearchParams(location.search);
  const openChatId = searchParams.get('openChat');
  
  const [activeTab, setActiveTab] = useState<RiderTabType>('home');
  const [selectedFilter, setSelectedFilter] = useState<DestinationType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [rideBoardTab, setRideBoardTab] = useState<'available' | 'post' | 'posted'>('available');
  
  const queryClient = useQueryClient();
  
  // Call useRealtime at TOP LEVEL (not inside useEffect) - this is a React hook!
  // It will only activate when currentUser.id exists
  useRealtime(currentUser?.id);

  const { data: rides = [], isLoading: ridesLoading } = useQuery({
    queryKey: ['availableRides', currentUser?.id],
    queryFn: ridesApi.getAvailableRides,
    enabled: !!currentUser?.id,
    refetchInterval: 10000,
  });

  const { data: myRequests = [] } = useQuery({
    queryKey: ['myRequests', currentUser?.id],
    queryFn: ridesApi.getMyRequests,
    enabled: !!currentUser?.id,
    refetchInterval: 10000,
  });

  const { data: myRidePosts = [], isLoading: myRidePostsLoading } = useQuery({
    queryKey: ['riderRideRequests', 'mine', currentUser?.id],
    queryFn: riderRideRequestsApi.getMine,
    enabled: !!currentUser?.id,
    refetchInterval: 10000,
  });

  const createRidePostMutation = useMutation({
    mutationFn: (payload: CreateRiderRideRequestPayload) => riderRideRequestsApi.create(payload),
    onSuccess: () => {
      toast.success('Ride request posted for nearby drivers.');
      queryClient.invalidateQueries({ queryKey: ['riderRideRequests'] });
      setRideBoardTab('posted');
    },
    onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to post ride request')),
  });

  const deleteRidePostMutation = useMutation({
    mutationFn: riderRideRequestsApi.remove,
    onSuccess: () => {
      toast.success('Ride request deleted.');
      queryClient.invalidateQueries({ queryKey: ['riderRideRequests'] });
    },
    onError: (error: unknown) => toast.error(getApiErrorMessage(error, 'Failed to delete ride request')),
  });

  const { data: conversations = [] } = useQuery({
    queryKey: ['conversations', currentUser?.id],
    queryFn: messagesApi.getConversations,
    enabled: !!currentUser?.id,
    refetchInterval: 5000, // Poll every 5 seconds for new conversations
  });

  const { data: posts = [] } = useQuery({
    queryKey: ['posts'],
    queryFn: feedApi.getPosts,
    enabled: activeTab === 'feeds',
  });

  // Fetch notifications for badge count
  const { data: notifications = [] } = useQuery({
    queryKey: ['notifications'],
    queryFn: notificationsApi.getNotifications,
    refetchInterval: 15000,
  });

  const unreadNotificationsCount = notifications.filter((n: any) => !n.read).length;

  const requestSeatMutation = useMutation({
    mutationFn: ridesApi.requestSeat,
    onSuccess: () => {
      toast.success('Seat requested! Waiting for driver approval.');
      queryClient.invalidateQueries({ queryKey: ['availableRides'] });
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to request seat'));
    },
  });

  // Feed post mutation - added for creating posts
  const createPostMutation = useMutation({
    mutationFn: (data: any) => feedApi.createPost(data),
    onSuccess: () => {
      toast.success('Post created successfully!');
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      setShowPostModal(false);
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to create post'));
    },
  });

  // Fetch current user profile for fresh data
  const { data: freshUserData } = useQuery({
    queryKey: ['currentUserProfile', currentUser?.id],
    queryFn: () => profileApi.getProfile(currentUser!.id),
    enabled: !!currentUser?.id,
    staleTime: 0, // Always consider stale to ensure fresh data
  });

  // Use fresh user data if available, otherwise fall back to currentUser prop
  // IMPORTANT: We must merge freshUserData with currentUser to ensure avatar and other fields
  // are preserved, since the API response might not include all fields (e.g., avatar)
  const displayUser: UserProfile = freshUserData 
    ? { ...currentUser, ...freshUserData } 
    : currentUser;

  // Profile update mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: any) => {
      const result = await profileApi.updateProfile(data);
      // Ensure avatar is included in the result - use the form data if API doesn't return it
      return {
        ...result,
        avatar: result.avatar || data.avatar || currentUser.avatar
      };
    },
    onSuccess: (updatedUser) => {
      // Update localStorage with new user data
      localStorage.setItem('collegeryde_user', JSON.stringify(updatedUser));
      // Force a refetch of the user data to update UI immediately
      queryClient.invalidateQueries({ queryKey: ['currentUserProfile'] });
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      // Call the onUserUpdate callback to update parent state
      onUserUpdate?.(updatedUser);
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to update profile'));
    },
  });

  // Calculate total unread messages from all conversations
  const unreadMessagesCount = conversations.reduce(
    (total, conv) => total + (conv.unreadCount || 0), 0
  );

  // Handle opening chat from URL query param (e.g., from profile page)
  useEffect(() => {
    if (openChatId && conversations.length > 0) {
      const conversation = conversations.find(c => c.id === openChatId);
      if (conversation) {
        setSelectedConversation(conversation);
        setIsChatOpen(true);
        setActiveTab('messages');
        // Clear the URL param
        navigate(location.pathname, { replace: true });
      }
    }
  }, [openChatId, conversations, navigate, location.pathname]);

  const startConversationMutation = useMutation({
    mutationFn: ({ userId, rideId }: { userId: string; rideId: string }) => 
      messagesApi.startConversation({ userId, rideId }),
    onSuccess: (conversation) => {
      setSelectedConversation(conversation);
      setIsChatOpen(true);
      setActiveTab('messages');
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: () => {
      toast.error('Failed to start conversation');
    },
  });

  const filteredRides = rides.filter((ride: Ride) => {
    const rideStatus = ride.tripStatusRider || ride.tripStatus || ride.status;
    const driverName = ride.driver?.name ?? 'Unknown User';
    if (rideStatus === 'ended' || rideStatus === 'cancelled') return false;
    if (selectedFilter !== 'all' && ride.destination !== selectedFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        ride.destinationName.toLowerCase().includes(query) ||
        ride.pickupLocation.toLowerCase().includes(query) ||
        driverName.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const getExistingRequest = (rideId: string) =>
    myRequests.find((request) => request.rideId === rideId);

  const hasAcceptedRideRequest = (rideId: string) => {
    const existingRequest = getExistingRequest(rideId);
    return existingRequest?.status === 'accepted';
  };

  const handleRequestSeat = (ride: Ride) => {
    const existingRequest = getExistingRequest(ride.id);
    if (existingRequest?.status === 'accepted') {
      toast.info('You already have a seat on this ride.');
      return;
    }

    requestSeatMutation.mutate(ride.id);
  };

  const handleStartConversation = async (ride: Ride) => {
    const driverId = ride.driver?.id;
    if (!driverId) {
      toast.error('Driver information is unavailable for this ride.');
      return;
    }

    const existingConversation =
      conversations.find((conversation) =>
        conversation.ride?.id === ride.id &&
        conversation.participants.some((participant) => participant.id === driverId)
      ) ||
      conversations.find((conversation) =>
        conversation.participants.some((participant) => participant.id === driverId)
      );

    if (existingConversation) {
      setSelectedConversation(existingConversation);
      setIsChatOpen(true);
      setActiveTab('messages');
      return;
    }

    startConversationMutation.mutate({ userId: driverId, rideId: ride.id });
  };

  const handleViewDriverProfile = (driverId?: string) => {
    if (!driverId) {
      toast.error('Driver profile is unavailable for this ride.');
      return;
    }

    navigate(`/profile/${driverId}`);
  };

  const handleViewRiderProfile = (riderId: string) => {
    navigate(`/profile/${riderId}`);
  };

  const handleViewProfile = (userId: string) => {
    if (userId) navigate(`/profile/${userId}`);
  };

  const handleTabChange = (tab: RiderTabType) => {
    setActiveTab(tab);
    if (tab !== 'messages') {
      setIsChatOpen(false);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'home':
        return (
          <div className="space-y-5 animate-fade-in">
            <Tabs value={rideBoardTab} onValueChange={(value) => setRideBoardTab(value as typeof rideBoardTab)}>
              <TabsList className="grid h-auto w-full grid-cols-3 rounded-xl p-1">
                <TabsTrigger value="available" className="rounded-lg">Available</TabsTrigger>
                <TabsTrigger value="post" className="rounded-lg">Post</TabsTrigger>
                <TabsTrigger value="posted" className="rounded-lg">Posted rides</TabsTrigger>
              </TabsList>

              <TabsContent value="available" className="mt-5 space-y-5">
              <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Where are you headed?"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 rounded-2xl border border-border bg-card outline-none shadow-warm focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <FilterChips selectedFilter={selectedFilter} onFilterChange={setSelectedFilter} />
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg">Available Rides</h2>
              <button 
                onClick={() => queryClient.invalidateQueries({ queryKey: ['availableRides', currentUser?.id] })}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors p-2 rounded-lg hover:bg-muted"
              >
                ↻ Refresh
              </button>
            </div>
            <div className="space-y-4">
              {ridesLoading ? (
                <div className="flex justify-center py-12">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filteredRides.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No rides available right now.</p>
                  <p className="text-sm text-muted-foreground/60 mt-1">Check back later!</p>
                </div>
              ) : (
                filteredRides.map((ride: Ride) => {
                  const existingRequest = getExistingRequest(ride.id);
                  const hasAcceptedRequest = hasAcceptedRideRequest(ride.id);

                  return (
                  <RideCard 
                    key={ride.id} 
                    ride={ride} 
                    statusMode="rider"
                    onRequestSeat={() => handleRequestSeat(ride)}
                    onMessageDriver={() => handleStartConversation(ride)}
                    onViewDriverProfile={(driverId) => handleViewDriverProfile(driverId ?? ride.driver?.id)}
                    showRequestButton={ride.seatsAvailable > 0}
                    requestButtonLabel={hasAcceptedRequest ? 'Joined' : 'Request'}
                    requestButtonDisabled={hasAcceptedRequest}
                    isFull={ride.seatsAvailable === 0}
                  />
                  );
                })
              )}
            </div>
              </TabsContent>

              <TabsContent value="post" className="mt-5">
                <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm">
                  <div className="mb-4">
                    <h2 className="font-display text-lg font-bold">Request a ride</h2>
                    <p className="text-sm text-muted-foreground">Tell drivers where you need to go and when you are available.</p>
                  </div>
                  <RiderRideRequestForm
                    onSubmit={(payload) => createRidePostMutation.mutateAsync(payload).then(() => undefined)}
                    isSubmitting={createRidePostMutation.isPending}
                  />
                </div>
              </TabsContent>

              <TabsContent value="posted" className="mt-5 space-y-4">
                <div>
                  <h2 className="font-display text-lg font-bold">Your posted requests</h2>
                  <p className="text-sm text-muted-foreground">Manage requests that drivers can respond to.</p>
                </div>
                {myRidePostsLoading ? (
                  <div className="flex justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>
                ) : myRidePosts.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">You have not posted a ride request yet.</div>
                ) : myRidePosts.map((request) => (
                  <RiderRideRequestCard
                    key={request.id}
                    request={request}
                    onDelete={() => deleteRidePostMutation.mutate(request.id)}
                  />
                ))}
              </TabsContent>
            </Tabs>
            <SafetyFooter />
          </div>
        );
      case 'feeds':
        return <FeedsTab posts={posts} onCreatePost={() => setShowPostModal(true)} currentUser={currentUser} />;
      case 'messages':
        return (
          <ConversationList 
            conversations={conversations} 
            onSelectConversation={(c) => { 
              setSelectedConversation(c); 
              setIsChatOpen(true); 
            }}
            onViewProfile={handleViewProfile}
            currentUserId={currentUser?.id}
          />
        );
      case 'profile':
        return (
          <div className="space-y-6 animate-fade-in">
            <ProfileCard user={displayUser} isOwnProfile onEdit={() => setShowProfileEdit(true)} />
            <button 
              onClick={onLogout}
              className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border border-destructive/20 text-destructive hover:bg-destructive/5 transition-colors font-medium"
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="container max-w-lg mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="p-2 gradient-warm rounded-xl shadow-warm">
              <Leaf className="w-5 h-5 text-primary-foreground" />
            </div>
            <h1 className="font-display font-bold text-xl">CollegeRyde</h1>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowNotifications(true)}
              className="p-2 rounded-full hover:bg-muted relative"
            >
              <Bell className="w-5 h-5 text-muted-foreground" />
              {unreadNotificationsCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                  {unreadNotificationsCount > 9 ? '9+' : unreadNotificationsCount}
                </span>
              )}
            </button>
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-accent/10 text-accent">
              RIDER
            </span>
          </div>
        </div>
      </header>

      <main className="container max-w-lg mx-auto px-4 py-5 pb-24">
        {renderContent()}
      </main>

      <BottomNav<RiderTabType>
        activeTab={activeTab} 
        onTabChange={handleTabChange}
        availableTabs={RIDER_TABS}
        badgeCounts={{ messages: unreadMessagesCount }}
      />
      
      <ChatPanel
        conversation={selectedConversation}
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        currentUser={currentUser}
      />

      <NotificationsPanel 
        isOpen={showNotifications} 
        onClose={() => setShowNotifications(false)}
        currentUserRole="rider"
        onViewRides={() => setActiveTab('home')}
        onViewConversation={(conversationId) => {
          const conversation = conversations.find(c => c.id === conversationId);
          if (conversation) {
            setSelectedConversation(conversation);
            setIsChatOpen(true);
            setActiveTab('messages');
          }
        }}
      />

      <PostModal
        isOpen={showPostModal}
        onClose={() => setShowPostModal(false)}
        user={currentUser}
        onSubmit={async (data) => {
          await createPostMutation.mutateAsync(data);
        }}
      />

      <ProfileEditModal
        isOpen={showProfileEdit}
        onClose={() => setShowProfileEdit(false)}
        user={displayUser}
        onSave={async (data) => {
          await updateProfileMutation.mutateAsync(data);
          // Force a UI refresh by manually updating the local display user state
          // This ensures the avatar shows up immediately after saving
        }}
      />
    </div>
  );
};

export default RiderDashboard;
